import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import UiInput from 'components/Ui/UiInput'
import CompanyFormLabel from 'pages/Front/Booking/components/CompanyForm/CompanyFormLabel'
import clsx from 'clsx'
import { FieldErrors } from 'react-hook-form'
import useCompanyPositionOptions, {
    defaultPositions,
} from '../hooks/useCompanyPositionOptions'

interface CompanyPositionSelectProps {
    value: string | null
    onChange: (value: string | null) => void
    error?: string
    className?: string
    required?: boolean
    errors?: FieldErrors
}

const CompanyPositionSelect: React.FC<CompanyPositionSelectProps> = ({
    value,
    onChange,
    error,
    className,
    required,
    errors,
}) => {
    const { t } = useTranslation()
    const options = useCompanyPositionOptions(t('Other'))
    const normalizedValue = (value ?? '').trim()
    const [customModeActive, setCustomModeActive] = useState(false)

    const selectedOption = useMemo(() => {
        if (normalizedValue && defaultPositions.includes(normalizedValue)) {
            return normalizedValue
        }
        if (normalizedValue) {
            return 'custom'
        }
        if (customModeActive) {
            return 'custom'
        }
        return null
    }, [normalizedValue, customModeActive])

    const customValue = selectedOption === 'custom' ? normalizedValue : ''

    const handleSelectChange = (val: string | number | null) => {
        const stringVal = val ? String(val) : null
        if (stringVal === 'custom') {
            setCustomModeActive(true)
            onChange('')
            return
        }

        setCustomModeActive(false)
        onChange(stringVal ?? '')
    }

    const handleCustomValueChange = (val: string) => {
        setCustomModeActive(true)
        onChange(val.trim() || '')
    }

    const displayLabel =
        selectedOption && selectedOption !== 'custom'
            ? selectedOption
            : customValue.trim() || normalizedValue || ''

    return (
        <div className={clsx('CompanyPositionSelect flex flex-col', className)}>
            <CompanyFormLabel
                label={t('Position')}
                name="position"
                required={required}
                errors={errors || {}}
            />
            <UiSelectWithGraphics
                label=""
                options={options}
                selected={selectedOption}
                setSelected={handleSelectChange}
                debounceMs={0}
                title={displayLabel || undefined}
                dropdownMinWidth={320}
                wrapOptionLabels
            />
            {selectedOption === 'custom' && (
                <UiInput
                    placeholder={t('Enter custom position')}
                    value={customValue}
                    onChange={(e) => handleCustomValueChange(e.target.value)}
                    hasError={!!error}
                    autoComplete="one-time-code"
                />
            )}
            {!!error && (
                <span className="text-xs text-red-500 mt-1">{error}</span>
            )}
        </div>
    )
}

export default CompanyPositionSelect
